import {
  DTConsumer_Private,
  DTHoldingProxy,
  FBD_Category_Public,
  FBD_HoldingProxy,
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
  FBD_Vendable,
  FBD_CaseFlowPrivateProxy,
  DTCaseFlowPrivateProxy,
  DTVendable,
  PersonaIdTypeSplitter,
} from '@rabbit/data/types';
import { useEffect, useState } from 'react';
import { CFC_FetchConsumerPrivate_WithId } from '@rabbit/bizproc/core';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import { t } from 'i18next';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { query, where, limit } from 'firebase/firestore';
import {
  DR_Holding_Proxy,
  DR_Vendable,
  DR_Category_Public,
} from '@rabbit/data/portal';

export interface SageConsumerDataShape {
  consumer_private?: DTConsumer_Private | null;
  holding_proxy?: DTHoldingProxy | null;
  allHoldings: DTHoldingProxy[] | null;
  claims: DTCaseFlowPrivateProxy[];
  vendable?: DTVendable | null;
  holdingClaims: DTCaseFlowPrivateProxy[];
}
/**
 * Fetches a consumer's data from a holding or consumer ID. If a holding ID is used,
 * it will also return the holding proxy for that holding.
 *
 * NOTE: There are other hooks that also do consumer data fetching, but they might become deprecated in the future
 * It's recommended to use this one unless you have a specific reason not to.
 * @param holdingId
 * @param consumerId
 * @returns
 */
export function useGetConsumerData(
  holdingId?: string | null,
  consumerId?: string,
  shouldRefetch?: number,
  premiumTenant?: string
) {
  const [consumerData, setConsumerData] =
    useState<SageConsumerDataShape | null>(null);

  if (!holdingId && !consumerId)
    throw new Error('Must provide a holding or consumer ID');

  const holdingProxyQuery = holdingId
    ? query(
        DR_Holding_Proxy.collection(),
        where('docid', '==', holdingId),
        where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            premiumTenant
        )
      )
    : undefined;

  const holdingProxyResult = useFirestoreQueryData(
    ['holding-proxy', holdingId, shouldRefetch],
    holdingProxyQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!holdingId,
    }
  );

  const holding_proxy = holdingProxyResult?.data?.[0] || null;

  const vendableQuery = holding_proxy?.vendable
    ? query(
        DR_Vendable.collection(),
        where('docid', '==', holding_proxy.vendable),
        where(
          'mfr',
          '==',
          PersonaTypeSingleLetter.Manufacturer +
            PersonaIdTypeSplitter +
            premiumTenant
        )
      )
    : undefined;

  const vendableResult = useFirestoreQueryData(
    ['vendable', holding_proxy?.vendable, shouldRefetch],
    vendableQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!holding_proxy?.vendable,
    }
  );

  const vendable = vendableResult?.data?.[0] || null;
  const category = vendable?.category?.length
    ? vendable.category[vendable.category.length - 1]
    : null;

  const categoryQuery = category
    ? query(
        DR_Category_Public.collection(),
        where('key', '==', category),
        limit(1)
      )
    : undefined;

  const categoryResult = useFirestoreQueryData(
    ['category', category, shouldRefetch],
    categoryQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!category,
    }
  );

  const getData = async (): Promise<SageConsumerDataShape> => {
    let consumer_private: DTConsumer_Private | null | undefined;
    let claims: DTCaseFlowPrivateProxy[] = [];
    let holdingClaims: DTCaseFlowPrivateProxy[] = [];

    if (holdingId) {
      if (!holding_proxy) throw new Error('Holding proxy not found');

      const result = await CFC_FetchConsumerPrivate_WithId.call({
        consumerId: holding_proxy.consumer,
      });
      if (!result.ok) throw new Error(result.error);
      consumer_private = result.data.consumer_private;

      if (!consumer_private) throw new Error('Consumer not found');

      // Also fetch claims for this consumer when only holdingId is provided
      holdingClaims = await FBD_CaseFlowPrivateProxy.query()
        .where('facts.consumer_holding', '==', holding_proxy.holdingLink)
        .getContents();
    }

    if (consumerId && !holdingId) {
      const result = await CFC_FetchConsumerPrivate_WithId.call({
        consumerId,
      });
      if (!result.ok) throw new Error(result.error);
      consumer_private = result.data.consumer_private;
      if (!consumer_private) throw new Error('Consumer not found');
    }

    if (consumerId) {
      claims = await FBD_CaseFlowPrivateProxy.query()
        .where('facts.consumer_persona_id', '==', consumerId)
        .getContents();
    }

    let categoryTitle: string | null = null;
    if (holding_proxy?.category && vendable) {
      const categoryPublicData = categoryResult?.data?.[0];
      const categoryKey = vendable.category?.length
        ? vendable.category[vendable.category.length - 1]
        : '-';
      categoryTitle =
        categoryPublicData?.titles?.en || formatCategory(categoryKey);
    }

    // Create a copy of holding_proxy to avoid mutating the original
    const updatedHoldingProxy = holding_proxy
      ? {
          ...holding_proxy,
          category: categoryTitle ? [categoryTitle] : [],
        }
      : null;

    const allHoldings = consumer_private
      ? (await getAllHoldingProxies(consumer_private.docid)) ?? []
      : null;

    return {
      consumer_private,
      holding_proxy: updatedHoldingProxy,
      allHoldings,
      claims,
      vendable,
      holdingClaims,
    };
  };

  useEffect(() => {
    if (holdingId && !holdingProxyResult?.data) {
      return;
    }

    if (holding_proxy?.vendable && !vendableResult?.data) {
      return;
    }

    if (category && !categoryResult?.data) {
      return;
    }

    (async () => {
      const data = await getData();
      setConsumerData(data);
    })().catch((err) => console.error(err));
  }, [
    holdingId,
    shouldRefetch,
    holdingProxyResult?.data,
    vendableResult?.data,
    categoryResult?.data,
  ]);

  return consumerData;
}

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

export const getAllHoldingProxies = async (
  consumerId: string
): Promise<DTHoldingProxy[]> => {
  const proxyQuery = FBD_HoldingProxy.query().where(
    'consumer',
    '==',
    consumerId
  );

  const manufacturer = getRootPersonaFromLexicon(
    t(PrincipalsFieldName),
    PersonaTypeSingleLetter.Manufacturer
  );

  if (manufacturer) {
    proxyQuery.where('mfr', '==', manufacturer);
  }

  const holdingProxies = await proxyQuery.getContents();
  return holdingProxies;
};

const formatCategory = (categories: string) => {
  return categories
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char: string) => char.toUpperCase());
};
